import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, Settings, LogOut, Activity, Key, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useLivePrices from '../hooks/useLivePrices';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const livePrices = useLivePrices();
  const btc = livePrices['BTC/USD'];
  const btcPrice = btc?.price ? btc.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';
  const btcUp = (btc?.change ?? 0) >= 0;
  const btcChange = btc?.change != null ? Math.abs(btc.change).toFixed(2) : '—';

  const navItems = [
    { name: 'Tutorials', path: '/tutorials' },
    { name: 'Backtesting', path: '/backtesting' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
    { name: 'About', path: '/about' },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => { logout(); setShowProfileMenu(false); navigate('/'); };

  const getUserInitials = () => {
    if (!user?.name || user.name === 'undefined') return 'U';
    const n = user.name.trim().split(' ');
    return n.length >= 2 ? `${n[0][0]}${n[1][0]}`.toUpperCase() : n[0][0]?.toUpperCase() || 'U';
  };

  const getUserFirstName = () => {
    if (!user?.name || user.name === 'undefined') return 'User';
    return user.name.trim().split(' ')[0] || 'User';
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: scrolled
            ? 'rgba(6,6,8,0.98)'
            : 'rgba(6,6,8,0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          borderBottom: '1px solid rgba(56,254,96,0.1)',
          boxShadow: scrolled
            ? '0 4px 40px rgba(0,0,0,0.7), 0 0 80px rgba(56,254,96,0.04)'
            : '0 2px 20px rgba(0,0,0,0.5), 0 0 60px rgba(56,254,96,0.03)',
          transition: 'all 0.3s ease',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center h-[68px] gap-8">

                    <Link to="/" className="flex-shrink-0 flex items-center gap-3">
            <img src="/logo.png" alt="KiteKraken" className="h-9 w-auto" />
                        <span className="hidden lg:block w-px h-6 bg-[rgba(255,255,255,0.08)]" />
                        <div className="hidden lg:flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[#38FE60] animate-pulse" />
              <span className="text-[11px] font-mono text-[#777]">BTC</span>
              <span className="text-[12px] font-mono font-bold text-white" style={{ textShadow: '0 0 8px rgba(255,255,255,0.15)' }}>${btcPrice}</span>
              <span className={`text-[10px] font-mono font-bold ${btcUp ? 'text-[#38FE60]' : 'text-[#ff5252]'}`}>
                {btcUp ? '▲' : '▼'} {btcChange}%
              </span>
            </div>
          </Link>

                    <div className="flex-1" />

                    <div className="hidden md:flex items-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative h-[68px] flex items-center px-4 text-[11px] font-bold tracking-[0.12em] uppercase transition-colors duration-200 ${!isActive(item.path) ? 'nav-link-pulse' : ''}`}
                style={{
                  color: isActive(item.path) ? '#38FE60' : 'rgba(255,255,255,0.9)',
                  fontFamily: "'Lato', sans-serif",
                  textShadow: isActive(item.path) ? '0 0 20px rgba(56,254,96,0.9), 0 0 40px rgba(56,254,96,0.4)' : undefined,
                }}
                onMouseEnter={e => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.textShadow = '0 0 12px rgba(56,254,96,0.4), 0 0 28px rgba(56,254,96,0.15)';
                    e.currentTarget.style.animationPlayState = 'paused';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.textShadow = '';
                    e.currentTarget.style.animationPlayState = 'running';
                  }
                }}
              >
                {item.name}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full"
                    style={{ background: '#38FE60', boxShadow: '0 0 12px rgba(56,254,96,0.8), 0 0 24px rgba(56,254,96,0.3)' }} />
                )}
              </Link>
            ))}
            {isAuthenticated && (
              <Link to="/api-settings"
                className="h-[68px] flex items-center px-4 text-[11px] font-bold tracking-[0.12em] uppercase transition-colors"
                style={{
                  color: isActive('/api-settings') ? '#38FE60' : 'rgba(255,255,255,0.75)',
                  textShadow: isActive('/api-settings') ? '0 0 16px rgba(56,254,96,0.6)' : 'none',
                }}>
                API
              </Link>
            )}
          </div>

                    <span className="hidden md:block w-px h-6 bg-[rgba(255,255,255,0.07)]" />

                    <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2.5 h-9 px-3 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="w-6 h-6 rounded bg-[#38FE60] flex items-center justify-center text-[#060608] text-[10px] font-black">
                    {getUserInitials()}
                  </div>
                  <span className="text-[12px] text-white font-medium">{getUserFirstName()}</span>
                  <ChevronDown className={`w-3 h-3 text-[#444] transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 top-full mt-2 w-52 rounded-xl overflow-hidden z-50"
                    style={{ background: '#0a0a0c', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 24px 60px rgba(0,0,0,0.9)' }}>
                    <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <p className="text-white text-[13px] font-semibold">{user?.name || 'User'}</p>
                      <p className="text-[#444] text-[11px] mt-0.5">{user?.email || ''}</p>
                    </div>
                    <div className="py-1">
                      {[
                        { to: '/profile', icon: User, label: 'Profile' },
                        { to: '/api-settings', icon: Key, label: 'API Credentials' },
                        { to: '/profile?tab=trading', icon: Activity, label: 'Trading Activity' },
                        { to: '/profile?tab=settings', icon: Settings, label: 'Settings' },
                      ].map(({ to, icon: Icon, label }) => (
                        <Link key={to} to={to} onClick={() => setShowProfileMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-[12px] text-[rgba(255,255,255,0.45)] hover:text-white hover:bg-[rgba(255,255,255,0.04)] transition-all">
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </Link>
                      ))}
                    </div>
                    <div className="py-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <button onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 text-[12px] text-[#ff5252] hover:bg-[rgba(255,82,82,0.06)] transition-all w-full">
                        <LogOut className="w-3.5 h-3.5" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/auth">
                <button
                  className="relative h-10 px-6 text-[12px] font-black tracking-[0.1em] uppercase overflow-hidden"
                  style={{
                    background: '#38FE60',
                    color: '#060608',
                    fontFamily: "'Lato', sans-serif",
                    clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
                    boxShadow: '0 0 24px rgba(56,254,96,0.3)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 36px rgba(56,254,96,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 24px rgba(56,254,96,0.3)'; e.currentTarget.style.transform = 'none'; }}
                >
                  Get Started
                </button>
              </Link>
            )}
          </div>

                    <button onClick={() => setIsOpen(!isOpen)}
            className="md:hidden flex items-center justify-center w-8 h-8 rounded"
            style={{ color: '#fff', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)' }}>
            {isOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </nav>

            {isOpen && (
        <div className="md:hidden fixed top-[68px] left-0 right-0 z-40"
          style={{ background: '#060608', borderBottom: '1px solid rgba(56,254,96,0.1)' }}>
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}
                className="flex items-center px-4 py-3 rounded text-[12px] font-bold tracking-[0.1em] uppercase transition-all"
                style={{
                  color: isActive(item.path) ? '#38FE60' : 'rgba(255,255,255,0.5)',
                  background: isActive(item.path) ? 'rgba(56,254,96,0.06)' : 'transparent',
                  borderLeft: isActive(item.path) ? '2px solid #38FE60' : '2px solid transparent',
                }}>
                {item.name}
              </Link>
            ))}
            {!isAuthenticated && (
              <Link to="/auth" onClick={() => setIsOpen(false)} className="mt-3">
                <button className="w-full h-11 text-[12px] font-black tracking-[0.12em] uppercase"
                  style={{ background: '#38FE60', color: '#060608', clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)' }}>
                  Get Started
                </button>
              </Link>
            )}
            {isAuthenticated && (
              <>
                <div className="flex items-center gap-3 px-4 py-3 mt-2 rounded"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="w-8 h-8 rounded bg-[#38FE60] flex items-center justify-center text-[#060608] font-black text-sm">
                    {getUserInitials()}
                  </div>
                  <div>
                    <p className="text-white text-sm font-bold">{user?.name || 'User'}</p>
                    <p className="text-[#444] text-xs">{user?.email || ''}</p>
                  </div>
                </div>
                <button onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#ff5252]">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
